/**
 * Query key factory for profile data
 * Provides type-safe, hierarchical query keys for cache management
 *
 * Following Bulletproof React pattern for API layer organization
 *
 * Hierarchy:
 * - profileQueryKeys.all: Root level cache key for all profile data
 * - profileQueryKeys.profile(): User profile with role-specific data
 * - profileQueryKeys.accessibility(): Accessibility preferences
 * - profileQueryKeys.email(): Email notification preferences
 * - profileQueryKeys.dataSummary(): Privacy data summary for export/deletion
 * - profileQueryKeys.management: Admin user management queries
 *
 * Usage Examples:
 * - Invalidate all profile data: queryClient.invalidateQueries({ queryKey: profileQueryKeys.all })
 * - Invalidate specific user's profile: queryClient.invalidateQueries({ queryKey: profileQueryKeys.profile(userId) })
 * - Invalidate accessibility settings: queryClient.invalidateQueries({ queryKey: profileQueryKeys.accessibility(userId) })
 *
 * Invalidation Cascades:
 * - Invalidating profileQueryKeys.all clears ALL profile-related queries
 * - Each user-scoped key is independent (invalidating one user doesn't affect others)
 * - Settings keys (accessibility, email) are siblings under the root, not nested under profile
 */

export const profileQueryKeys = {
  /**
   * Root level - all profile queries
   * Invalidating this key will clear ALL profile-related data from cache
   */
  all: ['profile'] as const,

  /**
   * User profile with role-specific data
   * Includes base profile (user_profiles) and role-specific data
   * (learner_user_profiles, employer_user_profiles, or platform_user_profiles)
   * @param userId - User UUID from auth.users
   * @example profileQueryKeys.profile('123e4567-e89b-12d3-a456-426614174000')
   */
  profile: (userId: string) =>
    [...profileQueryKeys.all, 'user', userId] as const,

  /**
   * Accessibility preferences for a user
   * Includes font size, reduced motion, high contrast, etc.
   * @param userId - User UUID from auth.users
   * @example profileQueryKeys.accessibility('123e4567-e89b-12d3-a456-426614174000')
   */
  accessibility: (userId: string) =>
    [...profileQueryKeys.all, 'accessibility', userId] as const,

  /**
   * Email notification preferences for a user
   * Includes marketing emails, workshop updates, etc.
   * @param userId - User UUID from auth.users
   * @example profileQueryKeys.email('123e4567-e89b-12d3-a456-426614174000')
   */
  email: (userId: string) =>
    [...profileQueryKeys.all, 'email', userId] as const,

  /**
   * Data summary for privacy tab (export/deletion)
   * Aggregates counts of user data across tables for GDPR compliance
   * @param userId - User UUID from auth.users
   * @example profileQueryKeys.dataSummary('123e4567-e89b-12d3-a456-426614174000')
   */
  dataSummary: (userId: string) =>
    [...profileQueryKeys.all, 'data-summary', userId] as const,

  /**
   * Admin user management queries
   * Relocated from hooks/admin/query-keys.ts as part of Bulletproof migration
   */
  management: {
    /**
     * All management queries
     */
    all: () => [...profileQueryKeys.all, 'management'] as const,

    /**
     * User profiles list for admin management
     * @param search - Optional search term
     */
    userProfiles: (search?: string) =>
      [...profileQueryKeys.management.all(), 'profiles', { search }] as const,

    /**
     * User email data for admin management
     * @param search - Optional search term
     */
    userEmailData: (search?: string) =>
      [...profileQueryKeys.management.all(), 'emailData', { search }] as const,

    /**
     * User activity logs
     * @param userId - User UUID
     * @param limit - Optional limit
     */
    userActivityLogs: (userId: string, limit?: number) =>
      [...profileQueryKeys.management.all(), userId, 'activityLogs', { limit }] as const,

    /**
     * User by email lookup
     * @param email - User email
     */
    userByEmail: (email: string) =>
      [...profileQueryKeys.management.all(), 'byEmail', email] as const,
  },
} as const;
